import React, { MouseEvent, createRef, useContext, useRef, useEffect } from 'react';
import { AutoPlayContext } from 'src/context/AutoPlayContext';
import { useHover, useWindowSize } from 'src/hooks';
import * as S from './styles';

export interface MediaCardProps {
  /**
   * Link to the media attribute, either a photo or a
   * video on our S3 bucket. Can be empty string to
   * load nothing and show placeholder.
   */
  src?: string;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  title?: string;
  description?: string;
  skeletal?: boolean;
  className?: string;
}

export const isImage = (src: string) => src.match(/(\/images\/|\/img\/|uploads\/.*\.png)/) !== null;

/**
 * A card component which displays a video at larger sizes
 * or a simple row with text at smaller sizes
 */
export const MediaCard = ({
  src,
  title,
  description,
  onClick,
  skeletal = false,
  className,
}: MediaCardProps) => {
  const { handleAutoplayError, setAutoPlayRestricted } = useContext(AutoPlayContext);
  const videoRef = createRef<HTMLVideoElement>();
  const isMobile = useIsMobileSize();
  const isPhotoCard = isImage(src || '');

  const containerRef = useRef<HTMLButtonElement | HTMLDivElement>(null);
  const containerIsHovered = useHover(containerRef);

  const playButtonRef = useRef<HTMLButtonElement>(null);
  const playButtonIsHovered = useHover(playButtonRef);

  const isHovered = containerIsHovered || playButtonIsHovered;

  // We check here to see if the src prop is missing.
  // TS tells us that this _should_ be here but there might
  // be an error with the `/on_boarding` endpoint or similar
  // that causes us to have false confidence.
  /* istanbul ignore next: untested branch of code, please test */
  if (!src && !skeletal) {
    console.error('Missing src for MediaCard. Please ensure it gets loaded properly.');
  }

  useEffect(() => {
    if (skeletal || isMobile || isPhotoCard || !videoRef || !videoRef.current) {
      return;
    }
    /* istanbul ignore next: untested branch of code, please test */
    if (isHovered) {
      videoRef.current
        .play()
        .then(() => {
          setAutoPlayRestricted(false);
        })
        .catch(error => {
          handleAutoplayError(error);
        });
    } else {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [
    isMobile,
    isHovered,
    videoRef,
    isPhotoCard,
    setAutoPlayRestricted,
    handleAutoplayError,
    skeletal,
  ]);

  // Disable clicking behaviour and improve accessibility on
  // cards appropriately
  const SClickContainer = skeletal || !onClick ? S.DivContainer : S.ButtonContainer;

  return (
    <S.Container className={className}>
      <SClickContainer
        // Just not worth it.
        // @ts-ignore
        ref={containerRef}
        onClick={onClick}
        data-testid={skeletal ? 'skeletal-card' : 'media-card'}
        $skeletal={skeletal}
      >
        {!isMobile &&
          (isPhotoCard ? (
            /* istanbul ignore next: untested branch of code, please test */ <S.Placeholder>
              <S.Image src={src} alt={title} />
            </S.Placeholder>
          ) : (
            <S.Placeholder>
              {
                // On iOS (e.g. iPads or landscape iPhones where isMobile is false) the first frame of
                // the video doesn't show. Setting the time with t= is a hack to fix this.
                // https://stackoverflow.com/questions/20075875/how-to-set-the-thumbnail-image-on-html5-video
              }
              <S.Video autoPlay={false} ref={videoRef} src={src + '#t=0.00001'} />
            </S.Placeholder>
          ))}
        <S.TextContainer>
          <S.TextBackground>
            {title && <S.Title>{title}</S.Title>}
            {description && <S.Description>{description}</S.Description>}
          </S.TextBackground>
        </S.TextContainer>
      </SClickContainer>
    </S.Container>
  );
};

function useIsMobileSize(): boolean {
  const { width } = useWindowSize();
  const rem = 16;
  return width < 30 * rem;
}

export default MediaCard;
